import { _routeHandlersShorthandsBase, Collection } from 'miragejs';
import {
  Response,
  _routeHandlersShorthandsBase as BaseShorthandRouteHandler,
  _routeHandlersShorthandsPost as PostShorthandRouteHandler,
} from 'miragejs';
import config from 'garaje/config/environment';
import { addDays, startOfDay } from 'date-fns';

export const SUBSCRIPTION_ESTIMATE_INCLUDES = [
  'current-invoice-estimate',
  'current-invoice-estimate.line-items',
  'current-invoice-estimate.discounts',
  'current-invoice-estimate.taxes',
  'next-invoice-estimate',
  'next-invoice-estimate.line-items',
  'next-invoice-estimate.discounts',
  'next-invoice-estimate.taxes',
].join(',');

export default function () {
  this.urlPrefix = config.billingApiHost;

  // **************************************************************************
  // Coupon redemptions
  // **************************************************************************
  this.post('/v1/coupon-redemptions', (schema, request) => {
    // This is the equivalent of super
    const handler = new PostShorthandRouteHandler(schema, this.serializerOrRegistry, 'couponRedemption');
    const _couponRedemption = handler.handleStringShorthand(request, schema.couponRedemptions);

    // Billing API returns 204 as defined at
    // https://jsonapi.org/format/#crud-creating-responses-204
    return new Response(204, {}, '');
  });

  // **************************************************************************
  // Payments
  // **************************************************************************
  this.get('/v1/payments', function (schema, request) {
    const collection = schema.payments.all();

    const { queryParams } = request;
    const pageNumber = parseInt(queryParams['page[number]'], 10) || 1;
    const pageSize = parseInt(queryParams['page[size]'], 10) || 20;
    const start = pageNumber * pageSize - pageSize;
    const end = pageNumber * pageSize;

    const recordCount = collection.models.length;
    const pageCount = Math.ceil(recordCount / pageSize);
    const paginatedModels = collection.models.slice(start, end);

    const paginatedCollection = new Collection('payments', paginatedModels);

    const response = this.serializerOrRegistry.serialize(paginatedCollection, request);

    response.meta = {
      'record-count': recordCount,
      'page-count': pageCount,
    };

    return response;
  });
  this.get('/v2/payments');

  // **************************************************************************
  // Payment Pdfs
  // **************************************************************************
  this.get('/v1/payment_pdfs');
  this.get('/v2/payment_pdfs');

  // **************************************************************************
  // Payment sources
  // **************************************************************************
  this.patch('/v1/payment-sources/:id');
  this.get('/v1/payment-sources', (schema, request) => {
    // The API includes these automatically
    const companyId = request.queryParams['filter[company-id]'];
    return schema.paymentSources.where({ companyId });
  });
  this.post('/v1/payment-sources', (schema, request) => {
    const handler = new BaseShorthandRouteHandler(schema, this.serializerOrRegistry);
    const paymentSource = schema.paymentSources.create(handler._getAttrsForRequest(request, 'payment-source'));
    // Add CC information
    paymentSource.update({ brand: 'visa', last4: 1234 });

    return paymentSource;
  });

  // **************************************************************************
  // TODO: Location Billing: do not remove
  // **************************************************************************

  // TODO: come back to this endpoint when we test location billing subscritpion summary modal
  this.post('/v2/location-subscription-estimates');

  this.get('/v2/subscription-batches/:id', (schema) => {
    return schema.subscriptionBatches.create({
      status: 'success',
    });
  });
  this.post('/v2/subscription-batches', (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const { attributes, relationships } = body.data;
    const company = schema.companies.find(relationships.company.data.id);
    const locations = schema.locations.where({ companyId: company.id });

    body.data.attributes.products.forEach((product) => {
      const subscriptionPlanId = product['subscription-plan-id'];
      const subscriptionPlan = schema.subscriptionPlans.find(subscriptionPlanId);
      const app = subscriptionPlan.app;
      const subscriptionPlanIdsForApp = schema.subscriptionPlans.where({ app }).models.map((plan) => plan.id);

      locations.models.forEach((location) => {
        const locationSubscriptionForApp = schema.locationSubscriptions.findBy((subscription) => {
          return (
            subscription.locationId === location.id &&
            subscriptionPlanIdsForApp.includes(subscription.subscriptionPlanId)
          );
        });
        const productLocation = product?.locations?.find((loc) => loc.id === location.id);

        if (locationSubscriptionForApp) {
          const attrs = { subscriptionPlanId, app };

          if (productLocation) {
            attrs.quantity = productLocation.quantity;
          }

          locationSubscriptionForApp.update(attrs);
        } else {
          const attrs = {
            company,
            location,
            subscriptionPlanId,
            app,
          };
          if (productLocation) {
            attrs.quantity = productLocation.quantity;
          }
          if (product.trial) {
            attrs.status = 'in_trial';
          }

          schema.locationSubscriptions.create(attrs);
        }
      });
    });

    return schema.subscriptionBatches.create({
      company: company,
      products: attributes.products,
      status: 'success',
    });
  });

  this.get('/v2/companies/:id', (schema, request) => {
    return schema.billingCompanies.find(request.params.id);
  });

  this.get('/v2/plans', function (schema) {
    return schema.subscriptionPlans.all();
  });
  this.get('/v2/plans/:id', function (schema, request) {
    return schema.subscriptionPlans.find(request.params.id);
  });

  this.get('/v2/subscriptions', (schema, request) => {
    const companyId = request.queryParams['filter[company_id]'];
    const locationId = request.queryParams['filter[location_id]'];
    const params = { companyId };
    if (locationId) {
      params.locationId = locationId;
    }

    return schema.locationSubscriptions.where(params);
  });

  this.get('/v1/estimates', function (schema) {
    return schema.estimates.create();
  });
  // **************************************************************************
  // TODO: Location Billing: do not remove
  // **************************************************************************

  // @belongsTo('location', { async: false }) location;
  // @attr('array') products;
  // @hasMany('subscription-estimate', { async: false }) subscriptionEstimates;

  // this.post('/v2/locatiob-subscription-estimates', (schema, request) => {
  //   const body = JSON.parse(request.requestBody);
  //   const { attributes, relationships } = body.data;
  //   const company = schema.companies.find(relationships.company.data.id);
  //   const location = schema.locations.where(relationships.location.data.id);

  //   body.data.attributes.products.forEach(product => {
  //     let subscriptionPlanId = product['subscription-plan-id'];
  //     let subscriptionPlan = schema.subscriptionPlans.find(subscriptionPlanId);
  //     let subscriptionPlanIdsForApp = schema.subscriptionPlans
  //       .where({ app: subscriptionPlan.app })
  //       .models.map(plan => plan.id);

  //     locations.models.forEach(location => {
  //       let locationSubscriptionForApp = schema.locationSubscriptions.findBy(subscription => {
  //         return (
  //           subscription.locationId === location.id &&
  //           subscriptionPlanIdsForApp.includes(subscription.subscriptionPlanId)
  //         );
  //       });
  //       let productLocation =
  //         product.hasOwnProperty('locations') && product.locations.find(loc => loc.id === location.id);

  //       if (locationSubscriptionForApp) {
  //         let attrs = { subscriptionPlanId };

  //         if (productLocation) {
  //           attrs.quantity = productLocation.quantity;
  //         }

  //         locationSubscriptionForApp.update(attrs);
  //       } else {
  //         let attrs = {
  //           company,
  //           location,
  //           subscriptionPlanId
  //         };
  //         if (productLocation) {
  //           attrs.quantity = productLocation.quantity;
  //         }
  //         if (product.trial) {
  //           attrs.status = 'in_trial';
  //         }

  //         schema.locationSubscriptions.create(attrs);
  //       }
  //     });
  //   });

  //   return schema.subscriptionBatches.create({
  //     company: company,
  //     products: attributes.products,
  //     status: 'success'
  //   });
  // });

  // this.get('/v2/invoice-estimates/:id', (schema, request) => {
  //   return schema.invoiceEstimates.find(request.params.id);
  // });

  // this.get('/v2/plans', function(schema) {
  //   return schema.subscriptionPlans.all();
  // });

  // this.get('/v2/plans/:id', function(schema, request) {
  //   return schema.subscriptionPlans.find(request.params.id);
  // });

  // this.get('/v2/subscriptions', (schema, request) => {
  //   let companyId = request.queryParams['filter[company_id]'];
  //   let locationId = request.queryParams['filter[location_id]'];
  //   let params = { companyId };
  //   if (locationId) {
  //     params.locationId = locationId;
  //   }

  //   return schema.locationSubscriptions.where(params);
  // });

  // **************************************************************************
  // Subscriptions
  // **************************************************************************
  this.get('/v1/subscriptions/:id');
  this.post('/v1/subscriptions', function (schema, request) {
    const { attributes, relationships } = JSON.parse(request.requestBody).data;
    const trialEndDate = addDays(startOfDay(new Date()), 14);
    const subscription = schema.subscriptions.create({
      app: attributes.app,
      plan: attributes.plan,
      companyId: relationships.company.data.id,
      trialEndDate,
    });

    if (['visitors', 'rooms'].includes(attributes.app)) {
      const company = schema.companies.find(relationships.company.data.id);
      company.update({ subscriptionId: subscription.id });
      // Mimic current behavior of the rooms API when creating a new subscription
      if (attributes.app === 'rooms') {
        company.locations.models.forEach((location) => {
          schema.roomLocationConfigs.create({ id: location.id });
        });
        schema.companyConfigs.create({ id: company.id });
      }
    }
    return subscription;
  });
  this.patch('/v1/subscriptions/:id', function (schema, request) {
    const attrs = this.normalizedRequestAttrs();
    const subscription = schema.subscriptions.find(request.params.id);
    if (attrs.companyId) {
      const company = schema.companies.find(attrs.companyId);
      // billing service kicks off event in envoy-web to set company to active. Simulating this here.
      company.update({ active: true });
    }

    subscription.update(attrs);
    return subscription;
  });

  this.post('/v1/subscriptions/:id/purchase', ({ subscriptions }, request) => {
    const subscription = subscriptions.find(request.params.id);
    const period = JSON.parse(request.requestBody).period;
    const quantity = JSON.parse(request.requestBody).quantity || subscription.quantity;

    subscription.update({
      subscribed: true,
      period,
      quantity,
      onTrial: false,
      onExpiredTrial: false,
      cancelled: false,
    });
    return subscription;
  });
  this.get('/v1/subscriptions', (schema, request) => {
    // The API includes these automatically
    const companyId = request.queryParams['filter[company_id]'];

    return schema.subscriptions.where({ companyId });
  });

  // **************************************************************************
  // Subscription estimates
  // **************************************************************************
  this.post('/v1/subscription-estimates', function (schema, request) {
    request.queryParams.include = SUBSCRIPTION_ESTIMATE_INCLUDES;
    const attrs = this.normalizedRequestAttrs();
    return schema.subscriptionEstimates.create(
      Object.assign(attrs, {
        currentInvoiceEstimate: schema.invoiceEstimates.create(),
        nextInvoiceEstimate: schema.invoiceEstimates.create({
          lineItems: [
            schema.estimateLineItems.create({
              entityType: 'plan',
            }),
          ],
        }),
      })
    );
  });

  // **************************************************************************
  // Coupon codes
  // **************************************************************************
  this.get('/v1/coupon-codes', function ({ couponCodes }, { queryParams }) {
    const companyFilter = queryParams['filter[company_id]'];
    const statusFilter = queryParams['filter[status]'];
    const planFilter = queryParams['filter[plan_id]'];

    return couponCodes.where(({ companyId, planId, status }) => {
      return (
        (companyFilter ? companyId === companyFilter : true) &&
        (statusFilter ? status === statusFilter : true) &&
        (planFilter ? planId === planFilter : true)
      );
    });
  });

  this.get('/v1/coupon-codes/:id');

  this.post('/v1/coupon-codes', function ({ couponCodes }, _request) {
    const attrs = this.normalizedRequestAttrs();

    return couponCodes.create(
      Object.assign(attrs, { durationType: 'limited_period', period: 12, periodUnit: 'month', status: 'not_redeemed' })
    );
  });

  // **************************************************************************
  // Features API
  // **************************************************************************
  this.get('/features', (schema, request) => {
    const companyId = request.queryParams['filter[company_id]'];
    const locationId = request.queryParams['filter[location_id]'];

    return schema.features.where({ company_id: companyId, location_id: locationId });
  });
}
